import { ConfigProvider, Table, Alert } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ruRU from 'antd/lib/locale/ru_RU'
import Filter from '../Filter/Filter'
import { CenteredSpin } from '../../../ui'
import BulletDot from '../../../ui/BulletDot/BulletDot'
import style from './style.m.less'
import useMount from '../../../hooks/useMount'
import { getFuelRegistry, getGosDict, getGosOwners } from '../../config'
import { renderDayMonthYear } from '../../../../utils/formatter'
import { monthPicker } from '../../../helpers'

import useRequest from '../../../hooks/useRequest'

export const getStatus = (option: number) => {
    switch (option) {
        case 1:
            return <div className={style.statusNew}>Новая</div>
        case 2:
            return <div className={style.statusNew}>На рассмотрении</div>
        case 3:
            return <div className={style.statusAgreed}>Согласована</div>
        case 4:
            return <div className={style.statusCancel}>Отказана</div>
        default:
            return <></>
    }
}

type PaginationType = {
    page: number
    pageSize: number
    filters?: any
}

const PAGE_SIZE = 10

const DEFAULT_PAG = {
    page: 1,
    pageSize: PAGE_SIZE,
}

const COLUMNS = (isOpen: boolean) => [
    {
        title: '',
        dataIndex: 'fuel_application_status_id',
        key: 'fuel_application_status_id',
        width: 20,
        render: (fuel_application_status_id: number) =>
            fuel_application_status_id === 1 ? <BulletDot color="#528EFF" /> : <></>,
    },
    {
        title: 'Отчетный период',
        dataIndex: 'month',
        key: 'month',
        render: (month: number, record: any) => `${monthPicker(month)}, ${record.year}`,
    },
    {
        title: 'ЗГО',
        dataIndex: 'generator',
        key: 'generator',
        render: (generator: any) => generator.name,
    },
    {
        title: 'Владелец ЗГО',
        dataIndex: 'user_type',
        key: 'user_type',
        render: (user_type: number, record: any) =>
            user_type === 1
                ? `ИП ${record.go_owner.last_name} ${record.go_owner.first_name} ${record.go_owner.middle_name}`
                : record.go_owner.company_name_short,
    },
    {
        title: 'Дата подачи',
        dataIndex: 'ts_created',
        key: 'ts_created',
        render: (date: Date) => renderDayMonthYear(date),
    },
    !isOpen
        ? {
              title: 'Статус',
              dataIndex: 'fuel_application_status_id',
              key: 'status',
              render: (fuel_application_status_id: number) => getStatus(fuel_application_status_id),
          }
        : {},
]

interface IDefault {
    currentTab: string
}

const Default: FC<IDefault> = ({ currentTab }) => {
    const navigate = useNavigate()
    const [pagination, setPagination] = useState<PaginationType>(DEFAULT_PAG)
    const [filters, setFilters] = useState<any>()
    const { fetch: fetchDict, result: dict, isLoading: isLoadingDict } = useRequest(getGosDict)
    const {
        fetch: fetchOwners,
        result: owners,
        isLoading: isLoadingOwners,
    } = useRequest(getGosOwners)
    const { fetch, result, isLoading } = useRequest(getFuelRegistry)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [isEmptyRegistry, setIsEmptyRegistry] = useState(false)

    const isOpen = currentTab === 'Открытые'

    const getData = ({ page = 1, pageSize = 10, filters }: PaginationType) => {
        fetch({
            body: {
                limit: pageSize,
                offset: (page - 1) * pageSize,
                filters,
            },
            onSuccess: (data) => {
                if (isFirstLoad) {
                    if (!data?.applications?.length) {
                        setIsEmptyRegistry(true)
                    }
                    setIsFirstLoad(false)
                } else {
                    if (data?.applications?.length) {
                        setIsEmptyRegistry(false)
                    }
                }
            },
        })
    }

    const updateFilters = (data: string | number | boolean | number[], key: string, obj?: any) => {
        const updatedFilter = {
            ...filters,
            ...(obj ? { ...obj } : { [key]: data }),
        }
        setFilters(updatedFilter)
        setPagination({ page: 1, pageSize: pagination.pageSize })
        getData({ page: 1, pageSize: pagination.pageSize, filters: updatedFilter })
    }

    useEffect(() => {
        fetchDict({ body: { need_for_open_apps: isOpen } })
        fetchOwners({ body: { need_for_open_apps: isOpen } })
    }, [currentTab])

    useEffect(() => {
        if (!dict || !owners) {
            return
        }
        const isOpenValues = isOpen ? [1, 2] : [3, 4]
        updateFilters(isOpenValues, 'fuel_application_status_ids')
    }, [dict, owners, currentTab])

    if (isLoadingDict || !dict || isLoadingOwners || !owners) {
        return <CenteredSpin />
    }

    if (isEmptyRegistry) {
        return (
            <Alert
                message={
                    isOpen
                        ? 'У вас пока нет новых заявок на согласование'
                        : 'У вас пока нет закрытых заявок'
                }
                className={style.infoAlert}
                type="info"
                showIcon
            />
        )
    }

    return (
        <div className={style.opened}>
            <Filter onChange={updateFilters} dict={dict} owners={owners} isOpen={isOpen} />
            <div className={style.table}>
                <ConfigProvider locale={ruRU}>
                    <Table
                        dataSource={result?.applications || []}
                        columns={COLUMNS(isOpen)}
                        rowClassName={(record) =>
                            record.fuel_application_status_id === 1 ? style.rowBold : ''
                        }
                        pagination={{
                            position: ['bottomLeft'],
                            onChange: (page, pageSize) => {
                                getData({ page, pageSize, filters })
                                setPagination({ page, pageSize })
                            },
                            total: result?.total,
                            current: pagination.page,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                        }}
                        onRow={(record) => ({
                            onClick: () => {
                                navigate('/fuel-card-admin/' + record.id)
                            },
                        })}
                        loading={isLoading}
                    />
                </ConfigProvider>
            </div>
        </div>
    )
}

export default Default
